import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {LoadingLine} from '../loadingLine/renderLoadingPanel.react';
import meQuery from '../queries/MeQuery';
import ChannelAddStep1 from '../channels/ChannelAddStep1.react';
import ChannelAddStep2 from '../channels/ChannelAddStep2.react';
import ChannelAddStep3 from '../channels/ChannelAddStep3.react';
import ChannelAddStep4 from '../channels/ChannelAddStep4.react';
import ChannelAddStep5 from '../channels/ChannelAddStep5.react';
import LastOrganisationId from '../app/LastOrganisationId';
import {
    getFirstChannelCreateStep,
    getNextChannelCreateStep,
} from '../channels/channelCreateSteps';
import { ChannelDef, Scenario } from '../../common/types/types';

const STEP_1 = 1;
const STEP_2 = 2;
const STEP_3 = 3;
const STEP_4 = 4;
const STEP_5 = 5;

interface ScenarioChannelConnectProps {
 cancel?: (...args: unknown[]) => unknown | boolean;
 channelDef: ChannelDef;
 chooserAlternative?: boolean;
 connectCallback?: (...args: unknown[]) => unknown;
 existingChannel?: any;
 organisationId?: string;
 scenario?: Scenario;
}

export default class ScenarioChannelConnect extends React.Component<ScenarioChannelConnectProps> {
    static propTypes = {
        cancel: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
        channelDef: PropTypes.object.isRequired,
        chooserAlternative: PropTypes.bool,
        connectCallback: PropTypes.func,
        existingChannel: PropTypes.object,
        organisationId: PropTypes.string,
        scenario: PropTypes.object,
    };

    static defaultProps = {
        chooserAlternative: false,
        organisationId: LastOrganisationId.get()
    };

    constructor(props) {
        super(props);

        this.queryConfig = {
            queries: meQuery,
            name: 'User',
            params: {
                channelDefId: props.channelDef.realId,
                organisationId: props.organisationId,
                scenarioId: props.scenario ? props.scenario.realId : null
            }
        };

        let initialStep = STEP_1;
        if (props.existingChannel && props.existingChannel.realId) {
            this.queryConfig.params.channelId = props.existingChannel.realId;
            initialStep = getFirstChannelCreateStep(props.existingChannel.channelDef || props.channelDef);
        }

        this.state = {
            step: initialStep
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (JSON.stringify(nextState) !== JSON.stringify(this.state)) {
            return true;
        }

        if (JSON.stringify(nextProps) !== JSON.stringify(this.props)) {
            return true;
        }

        return false;
    }

    renderStep1() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep1}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done && this.step !== this.state.step) {
                    LoadingLine.stop();
                    this.step = this.state.configurationStep;
                    return (<ChannelAddStep1 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep1Callback(channel);}} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep1Callback(channel) {
        this.queryConfig.params.channelId = channel.realId;

        this.setState({
            step: getFirstChannelCreateStep(channel.channelDef),
        });
    }

    renderStep2() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep2}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep2 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} connect createCallback={(channel) => {this.connectChannelStep2Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} step={2} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep2Callback(channel) {
        if (channel.activated) {
            return this.connectChannelStep5Callback(channel);
        }

        this.setState({
            step: getNextChannelCreateStep(channel.channelDef, STEP_2)
        });
    }

    renderStep3() {
        return (<Relay.Renderer
            Container={ChannelAddStep3}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep3 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep3Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep3Callback(channel) {
        if (!channel.activated) {
            this.setState({
                step: getNextChannelCreateStep(channel.channelDef, STEP_3)
            });
        }
        else {
            return this.connectChannelStep5Callback(channel);
        }
    }

    renderStep4() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep4}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep4 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep4Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} step={4} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep4Callback(channel) {
        if (!channel.activated) {
            this.setState({
                step: getNextChannelCreateStep(channel.channelDef, STEP_4)
            });
        }
        else {
            return this.connectChannelStep5Callback(channel);
        }
    }

    renderStep5() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep5}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep5 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep5Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep5Callback(channel) {
        this.props.connectCallback(channel);
    }

    render() {
        let content = null;

        switch (this.state.step) {
            case STEP_1:
                content = this.renderStep1();
                break;
            case STEP_2:
                content = this.renderStep2();
                break;
            case STEP_3:
                content = this.renderStep3();
                break;
            case STEP_4:
                content = this.renderStep4();
                break;
            case STEP_5:
                content = this.renderStep5();
                break;
            }

        return (
            <div>
                <LoadingLine />
                {content}
            </div>
        );
    }
}
